import { useState, useRef, useEffect, forwardRef, useImperativeHandle, type Ref } from 'react'
import { Howl } from 'howler'

export interface AudioPlayerHandle {
  playAudio: () => void
  pauseAudio: () => void
  stopAudio: () => void
}

interface AudioPlayerProps {
  audioUrl: string
  className?: string
  /** Optional: set true when this player becomes inactive (e.g., other tab active) */
  forceStop?: boolean
}

export const AudioPlayer = forwardRef(function AudioPlayer(
  { audioUrl, className = '', forceStop = false }: AudioPlayerProps,
  ref: Ref<AudioPlayerHandle>
) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const soundRef = useRef<Howl | null>(null)

  // Helpers
  const destroyHowl = () => {
    const s = soundRef.current
    if (s) {
      try {
        s.stop()
      } catch {
        // ignore
      }
      try {
        s.unload()
      } catch {
        // ignore
      }
    }
    soundRef.current = null
  }

  // Expose controls to parent
  useImperativeHandle(ref, () => ({
    playAudio: () => {
      if (!audioUrl) return
      if (isPlaying) return
      startPlayback()
    },
    pauseAudio: () => {
      if (soundRef.current) {
        soundRef.current.pause()
        setIsPlaying(false)
        setIsLoading(false)
      }
    },
    stopAudio: () => {
      destroyHowl()
      setIsPlaying(false)
      setIsLoading(false)
    },
  }))

  // Stop/reset when told (e.g., switching tabs)
  useEffect(() => {
    if (forceStop) {
      destroyHowl()
      setIsPlaying(false)
      setIsLoading(false)
    }
  }, [forceStop])

  // Cleanup on URL change or unmount
  useEffect(() => {
    return () => {
      destroyHowl()
      setIsPlaying(false)
      setIsLoading(false)
    }
  }, [audioUrl])

  const startPlayback = () => {
    setIsLoading(true)

    // If already have a Howl (same URL), reuse it; else build new
    let sound = soundRef.current
    if (!sound) {
      sound = new Howl({
        src: [audioUrl],
        html5: true, // better for larger/streamed files
        onload: () => {
          setIsLoading(false)
          sound!.play()
          setIsPlaying(true)
        },
        onend: () => {
          setIsPlaying(false)
        },
        onloaderror: (_id, err) => {
          console.error('Audio load error', err)
          setIsLoading(false)
          setIsPlaying(false)
          destroyHowl()
        },
        onplayerror: (_id, err) => {
          console.error('Audio play error', err)
          setIsLoading(false)
          setIsPlaying(false)
          destroyHowl()
        },
      })
      soundRef.current = sound
    } else {
      // already loaded before
      setIsLoading(false)
      sound.play()
      setIsPlaying(true)
    }
  }

  const handlePlayPause = () => {
    if (!audioUrl) return
    if (isPlaying) {
      // Toggle pause
      if (soundRef.current) {
        soundRef.current.pause()
        setIsPlaying(false)
        setIsLoading(false)
      }
    } else {
      startPlayback()
    }
  }

  if (!audioUrl) return null

  return (
    <button
      onClick={handlePlayPause}
      disabled={isLoading}
      className={[
        'hover:bg-blue-tint-5 active:bg-blue-tint-5 bg-blue-tint-3 flex items-center justify-center rounded-full pb-1 text-white transition-colors hover:cursor-pointer disabled:opacity-50',
        className,
        isPlaying ? 'animate-beat transform-gpu' : '',
        isPlaying || isLoading ? 'bg-blue-tint-5' : '',
      ].join(' ')}
    >
      {isLoading ? (
        <svg
          width="46"
          height="46"
          className="animate-spin"
          fill="none"
          stroke="#002d9c"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ) : isPlaying ? (
        <svg
          width="46"
          height="46"
          viewBox="0 0 46 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M38.7382 44.9764C36.8753 44.9764 35.3616 43.4612 35.3616 41.5998V30.1537C35.3616 28.2909 36.8767 26.7772 38.7382 26.7772C40.5996 26.7772 42.1147 28.2923 42.1147 30.1537V41.5998C42.1147 43.4626 40.601 44.9764 38.7382 44.9764ZM38.7382 27.8073C37.4448 27.8073 36.3917 28.8589 36.3917 30.1537V41.5998C36.3917 42.8932 37.4433 43.9462 38.7382 43.9462C40.033 43.9462 41.0846 42.8946 41.0846 41.5998V30.1537C41.0846 28.8603 40.0316 27.8073 38.7382 27.8073ZM7.26153 44.9764C5.40012 44.9764 3.88495 43.4612 3.88495 41.5998V30.1537C3.88495 28.2909 5.40012 26.7772 7.26153 26.7772C9.12295 26.7772 10.6381 28.2909 10.6381 30.1537V41.5998C10.6381 43.4626 9.12295 44.9764 7.26153 44.9764ZM7.26153 27.8073C5.96813 27.8073 4.91509 28.8589 4.91509 30.1537V41.5998C4.91509 42.8932 5.96813 43.9462 7.26153 43.9462C8.55494 43.9462 9.60797 42.8932 9.60797 41.5998V30.1537C9.60797 28.8603 8.55494 27.8073 7.26153 27.8073ZM44.9763 35.8768H43.9461V23C43.9461 11.4495 34.5503 2.0537 22.9998 2.0537C11.4494 2.0537 2.05358 11.4495 2.05358 23V35.8768H1.02344V23C1.02344 10.8815 10.8813 1.02356 22.9998 1.02356C35.1183 1.02356 44.9763 10.8815 44.9763 23V35.8768Z"
            fill="#002d9c"
          />
        </svg>
      ) : (
        <svg
          width="46"
          height="46"
          viewBox="0 0 46 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M38.7382 44.9764C36.8753 44.9764 35.3616 43.4612 35.3616 41.5998V30.1537C35.3616 28.2909 36.8767 26.7772 38.7382 26.7772C40.5996 26.7772 42.1147 28.2923 42.1147 30.1537V41.5998C42.1147 43.4626 40.601 44.9764 38.7382 44.9764ZM38.7382 27.8073C37.4448 27.8073 36.3917 28.8589 36.3917 30.1537V41.5998C36.3917 42.8932 37.4433 43.9462 38.7382 43.9462C40.033 43.9462 41.0846 42.8946 41.0846 41.5998V30.1537C41.0846 28.8603 40.0316 27.8073 38.7382 27.8073ZM7.26153 44.9764C5.40012 44.9764 3.88495 43.4612 3.88495 41.5998V30.1537C3.88495 28.2909 5.40012 26.7772 7.26153 26.7772C9.12295 26.7772 10.6381 28.2909 10.6381 30.1537V41.5998C10.6381 43.4626 9.12295 44.9764 7.26153 44.9764ZM7.26153 27.8073C5.96813 27.8073 4.91509 28.8589 4.91509 30.1537V41.5998C4.91509 42.8932 5.96813 43.9462 7.26153 43.9462C8.55494 43.9462 9.60797 42.8932 9.60797 41.5998V30.1537C9.60797 28.8603 8.55494 27.8073 7.26153 27.8073ZM44.9763 35.8768H43.9461V23C43.9461 11.4495 34.5503 2.0537 22.9998 2.0537C11.4494 2.0537 2.05358 11.4495 2.05358 23V35.8768H1.02344V23C1.02344 10.8815 10.8813 1.02356 22.9998 1.02356C35.1183 1.02356 44.9763 10.8815 44.9763 23V35.8768Z"
            fill="white"
          />
        </svg>
      )}
    </button>
  )
})
